import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import {
  Avatar,
  AvatarGroup,
  Box,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from "@mui/material";
import { DealRoleGuard } from "components/guards";
import { TooltipDiv } from "components/tooltips/TooltipDiv";
import { useDealParamsHooks, useFormatDateHook, useQueryHook } from "hooks";
import { useDealActions, useFileMetaSelector, useFileSelect, useViewActions } from "store";
import { FileSharedWith, IFile } from "store/types";

import { RoleName } from "../../constants";

export const FileMetadata: React.FC = (): JSX.Element => {
  const { dealId, fileId } = useDealParamsHooks();
  const { getFileMetaAction, invalidateFileMetaAction, downloadFileAction } = useDealActions();
  const { setShareDialog } = useViewActions();
  const format = useFormatDateHook();

  const { name, type, location, owner, upload_date, shared_with } = useFileMetaSelector();
  const file: IFile = useFileSelect(fileId) || ({} as IFile);

  const { loading, refetch } = useQueryHook(async () => getFileMetaAction(dealId, fileId!));

  const firstUpdate: React.MutableRefObject<boolean> = useRef<boolean>(true);
  const tableData = [
    { title: "Name", name: name },
    { title: "Type", name: type },
    { title: "Location", name: location },
    { title: "Owner", name: owner },
    { title: "Upload", name: upload_date }
  ];

  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    refetch();
  }, [fileId]);

  useEffect(() => {
    return () => {
      invalidateFileMetaAction();
    };
  }, []);

  const onDownload: () => void = useCallback(
    () =>
      downloadFileAction({
        fileId: fileId,
        fileName: name as string,
        dealId: dealId
      }),
    [fileId, name, dealId]
  );

  const commonRowCellStyles = {
    sx: {
      color: "#013951",
      position: "relative",
      borderLeft: "4px solid transparent",
      textAlign: "center",
      padding: "0 24px",
      verticalAlign: "middle",
      fontSize: "15px",
      border: "none",
      borderBottom: "1px solid #bbc5c7"
    }
  };
  return (
    <Container
      maxWidth={false}
      sx={{
        flexGrow: 1,
        overflow: "hidden",
        padding: "35px 70px !important"
        // margin: "35px 110px"
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
          color: "#0a3142"
        }}
      >
        <p>File info</p>
        <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <AvatarGroup
              sx={{
                "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 15 },
                marginRight: "10px"
              }}
              max={6}
            >
              {shared_with?.map((share: FileSharedWith, index: number) => (
                <TooltipDiv key={share.id + index} place="bottom" tooltipMessage={share.full_name}>
                  <Avatar src={share.profile_photo_url} />
                </TooltipDiv>
              ))}
            </AvatarGroup>
            <IconButton sx={{ padding: "8px", marginRight: "3px" }}>
              <PersonAddOutlinedIcon
                onClick={() =>
                  name &&
                  setShareDialog({
                    elements: [fileId],
                    type: "file_id",
                    shareName: name,
                    dealId: dealId,
                    onBeforeClose: () => refetch(),
                    itemToShare: file.is_private ? null : file
                  })
                }
                sx={{ width: "20px", height: "20px" }}
              />
            </IconButton>
            <div className="folder-metadata__divider" />
            <IconButton onClick={onDownload} sx={{ padding: "8px" }}>
              <FileDownloadOutlinedIcon sx={{ width: "20px", height: "20px" }} />
            </IconButton>
          </div>
        </DealRoleGuard>
      </Box>
      <Box
        sx={{
          overflow: "hidden",
          boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25)"
        }}
      >
        <Box
          sx={{
            height: "65px",
            padding: "0 25px",
            backgroundColor: "#edf1f9",
            color: "white",
            fontSize: "18px",
            display: "flex",
            alignItems: "center"
          }}
        >
          <Box
            sx={{
              color: "#0a3142",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            <TooltipDiv place="bottom" tooltipMessage={location}>
              <span style={{ width: "500px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                {loading ? "..loading" : `${location?.join(" > ")} > ${name}`}
              </span>
            </TooltipDiv>
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: "white",
            flexGrow: 1,
            overflow: "scroll"
          }}
        >
          <Box sx={{ overflow: "scroll" }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "none",
                borderRadius: 0,
                border: "1px solid #dae0e2",
                borderTop: "none",
                zIndex: 1
              }}
            >
              <Table
                sx={{
                  ".MuiTableCell-root": {
                    padding: "14px 26px",

                    ":first-child": {
                      maxWidth: "250px",
                      padding: "14px 0px 14px 17px",
                      textAlign: "left"
                    },
                    ":last-child": {
                      padding: "14px 0 14px 0",
                      textAlign: "left",
                      fontWeight: 500
                    }
                  }
                }}
                stickyHeader
              >
                <TableBody>
                  {tableData.map((value, idx) => (
                    <TableRow key={idx}>
                      <TableCell {...commonRowCellStyles}>
                        <span>{value.title}</span>
                      </TableCell>
                      <TableCell {...commonRowCellStyles}>
                        <span>{value.title === "Upload" ? format({ date: value.name as string }) : value.name}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};
